/** @jsx jsx */
import React, { useEffect, useRef, useState } from "react";
import { jsx, Text } from "theme-ui";
import { Box } from "@shared-components/Box/Box";
import { Heading } from "@shared-components/Heading/Heading";
import { Flex } from "@shared-components/Flex/Flex";

import {
  beerNameStyles,
  brandStyles,
  buttonStyles,
  resetPrevNextButton,
  smallFlexStyling,
  textContainerStyles,
  textStyles,
  width50,
  width70,
  leftArrowSpanStyles,
  rightArrowSpanStyles,
  buttonLeftStyle,
  buttonRightStyle,
} from "./styles";
import { Container } from "@shared-components/Container/Container";
import { Button } from "@shared-components/Button/Button";
import { useWindowSize } from "../../shared/hooks/useWindowSize/useWindowSize";
import { Icon } from "@shared-components/Icon/Icon";
import { BeerSlider } from "@modules/BeerSlider/BeerSlider";
import {
  ellipseStyles,
  leftArrowStyles,
  rightArrowStyles,
} from "@modules/BeerSlider/styles";
import MediaSliderArrow from "@svg-icons/media-slider-arrow.component.svg";
import MediaSliderEllipse from "@svg-icons/media-slider-ellipse.component.svg";
import { mainContainerStyles } from "@modules/MyBottleCollection/styles";
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../context/site/useSiteContext";
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map";
import { MediaSlide } from "src/carlsberg-headless/types/modules/MediaSlider";
import { motion } from "framer-motion";
import { AlgoliaBottle } from "../../carlsberg-headless/types/modules/digital-memories";

type BottleProps = {
  currentBottleIndex: number;
  handleBottleIndex: (direction: "prev" | "next" | undefined) => void;
  hits: AlgoliaBottle[];
  totalHits?: number;
  toggleDetail: () => void;
  isLastPage?: boolean;
  loading?: boolean;
  show: boolean;
  from?: "memories" | "kiosk";
};

export type ShelfIndicatorType = {
  type: "row" | "divider";
  count: number;
  activeSection?: number;
  activeShelf?: number;
};

const BottleDetails: React.FC<BottleProps> = ({
  currentBottleIndex,
  totalHits,
  handleBottleIndex,
  hits,
  toggleDetail,
  isLastPage,
  show,
  from,
}: BottleProps) => {
  const getScrollbarWidth = () => {
    return window.innerWidth - document.documentElement.clientWidth;
  };
  const ScrollRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();
  const isMobile = windowSize.width && windowSize.width < 768;
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (footer) {
      const footerCopy = footer.cloneNode(true) as HTMLElement;
      footerCopy.style.position = "initial";
      document.querySelector("#beerContainer")?.appendChild(footerCopy);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("popstate", () => {
      if (show) {
        toggleDetail();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const closeDetails = () => {
    if (from === "memories") {
      const user = location.href.split("#")[1];
      history.pushState(null, "", `${location.href.split("?")[0]}#${user}`);
    } else {
      history.pushState(null, "", `${location.href.split("#")[0]}`);
    }
    toggleDetail();
  };

  if (!hits || !hits[currentBottleIndex]) return null;

  const {
    brewery,
    brand,
    name,
    year,
    sizegroup,
    imported,
    country,
    countryDk,
    licence,
    abv,
    id,
    commentDk,
    commentEn,
  } = hits[currentBottleIndex];

  const [slides, setSlides] = useState<MediaSlide[]>([]);
  const [animate, setAnimate] = useState(true);

  useEffect(() => {
    setAnimate(true);
    setSlides([]);
    for (let index = 1; index < 7; index++) {
      const thumbImage = `https://res.cloudinary.com/bottlecollection/image/fetch/w_600,c_scale,a_ignore/https://storage.googleapis.com/cb-bottle-collection.appspot.com/bottles/${id}/${index}.JPG`;
      const image: MediaSlide = {
        cbKey: "",
        key: 1,
        alias: "",
        themes: [],
        taxonomies: [],
        shortCodes: [],
        primaryLink: null,
        secondaryLink: null,
        headline: "slide 1",
        content: "",
        sectionTitle: "",
        image: {
          alias: "imageAsset",
          url: "",
          height: 605,
          width: 607,
          extension: "png",
          size: 395349,
          fullUrl: thumbImage,
          altText: "",
          focalPoint: {
            left: 0.5,
            top: 0.5,
          },
        },
        video: null,
      };

      setSlides((oldSlides) => [...oldSlides, image]);
    }
  }, [currentBottleIndex]);

  const variants = {
    hidden: { opacity: 0, y: 10, ease: "easing" },
    show: {
      opacity: 1,
      y: 0,
      ease: "easing",
    },
  };

  const transition = {
    duration: 0.5,
  };

  return (
    <div
      ref={ScrollRef}
      id="beerContainer"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        maxHeight: "100%",
        bg: "white",
        opacity: "1",
        zIndex: "51",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          ...mainContainerStyles,
          ...{
            "&:before": {
              content: "''",
              position: "fixed",
              top: 0,
              left: 0,
              right: getScrollbarWidth(),
              height: ["0.9375rem", null, "30px"],
              bg: "white",
            },
          },
        }}
      >
        <Container
          sx={{
            padding: 0,
            "&:before": {
              content: [null, null, "''"],
              position: "absolute",
              left: [3, 6],
              top: [3, 6],
              bottom: [3, 6],
              width: "50%",
              backgroundColor: "lightGray",
              zIndex: -1,
            },
          }}
        >
          {hits?.[currentBottleIndex] && (
            <Box sx={{ width: "100%" }}>
              <Flex
                sx={{
                  width: "100%",
                  minHeight: ["auto", "calc(100vh - 60px)"],
                  flexDirection: ["column-reverse", null, "row"],
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "lightGray",
                    width: ["100%", "100%", "70%"],
                  }}
                >
                  <Box sx={textContainerStyles}>
                    {!isMobile && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 15,
                        }}
                      >
                        <button
                          disabled={currentBottleIndex === 0}
                          onClick={() => {
                            setAnimate(false);
                            handleBottleIndex("prev");
                          }}
                          sx={buttonStyles(currentBottleIndex === 0)}
                        >
                          <Box
                            className={"button-prev"}
                            sx={{
                              ...buttonLeftStyle,
                            }}
                          >
                            <Box sx={leftArrowSpanStyles}>
                              <Icon
                                sx={leftArrowStyles}
                                svg={MediaSliderArrow}
                              />
                            </Box>
                            <Icon sx={ellipseStyles} svg={MediaSliderEllipse} />
                          </Box>
                          {get(
                            DICTIONARY_MAP.kiosk.bottle_details_prev,
                            currentCulture
                          )}
                        </button>
                        {!isMobile && (
                          <Box sx={{ color: "darkGreen" }}>
                            {currentBottleIndex + 1} of {totalHits}
                          </Box>
                        )}
                        <button
                          disabled={
                            currentBottleIndex === hits.length - 1 && isLastPage
                          }
                          onClick={() => {
                            setAnimate(false);
                            handleBottleIndex("next");
                          }}
                          sx={buttonStyles(
                            currentBottleIndex === hits.length - 1 && isLastPage
                          )}
                        >
                          {get(
                            DICTIONARY_MAP.kiosk.bottle_details_next,
                            currentCulture
                          )}
                          <Box
                            className={"button-next"}
                            sx={{
                              ...buttonRightStyle,
                            }}
                          >
                            <Box sx={rightArrowSpanStyles}>
                              <Icon
                                sx={rightArrowStyles}
                                svg={MediaSliderArrow}
                              />
                            </Box>
                            <Icon sx={ellipseStyles} svg={MediaSliderEllipse} />
                          </Box>
                        </button>
                      </Box>
                    )}
                    <motion.div
                      variants={variants}
                      initial={"hidden"}
                      animate={animate ? "show" : "hidden"}
                      transition={animate ? transition : { duration: 0 }}
                    >
                      <Heading as={"h6"} sx={brandStyles}>
                        {brand}
                      </Heading>
                      <Heading as={"h2"} sx={beerNameStyles}>
                        {name}
                      </Heading>
                      <Box>
                        <Flex sx={{ flexDirection: "column" }}>
                          {(country || year) && (
                            <Flex sx={smallFlexStyling}>
                              {country && (
                                <Text sx={{ ...textStyles, ...width50 }}>
                                  {get(
                                    DICTIONARY_MAP.kiosk.kiosk_filters_country,
                                    currentCulture
                                  )}
                                  :{" "}
                                  <Text as="span">
                                    {currentCulture === "en-us"
                                      ? country
                                      : countryDk || "-"}
                                  </Text>
                                </Text>
                              )}
                              {year && (
                                <Text sx={{ ...textStyles, ...width50 }}>
                                  {get(
                                    DICTIONARY_MAP.kiosk.kiosk_filters_year,
                                    currentCulture
                                  )}
                                  : <span>{year || "-"}</span>
                                </Text>
                              )}
                            </Flex>
                          )}
                          {(sizegroup || abv) && (
                            <Flex sx={smallFlexStyling}>
                              {sizegroup && (
                                <Text sx={{ ...textStyles, ...width50 }}>
                                  {get(
                                    DICTIONARY_MAP.kiosk.kiosk_filters_size,
                                    currentCulture
                                  )}
                                  : <span>{sizegroup + " cl" || "-"}</span>
                                </Text>
                              )}
                              {abv && (
                                <Text sx={{ ...textStyles, ...width50 }}>
                                  {get(
                                    DICTIONARY_MAP.kiosk.kiosk_filters_alcohol,
                                    currentCulture
                                  )}
                                  : <span>{abv + "%" || "-"}</span>
                                </Text>
                              )}
                            </Flex>
                          )}
                          {(imported || licence) && (
                            <Flex sx={smallFlexStyling}>
                              {imported && (
                                <Text sx={{ ...textStyles, ...width50 }}>
                                  {get(
                                    DICTIONARY_MAP.kiosk
                                      .kiosk_filters_imported_by,
                                    currentCulture
                                  )}
                                  : <span>{imported || "-"}</span>
                                </Text>
                              )}
                              {licence && (
                                <Text sx={{ ...textStyles, ...width50 }}>
                                  {get(
                                    DICTIONARY_MAP.kiosk
                                      .kiosk_filters_licensed_by,
                                    currentCulture
                                  )}
                                  : <span>{licence || "-"}</span>
                                </Text>
                              )}
                            </Flex>
                          )}
                          {brewery && (
                            <Flex sx={smallFlexStyling}>
                              <Text sx={{ ...textStyles, ...width70 }}>
                                {get(
                                  DICTIONARY_MAP.kiosk.kiosk_filters_brewery,
                                  currentCulture
                                )}
                                : <span>{brewery || "-"}</span>
                              </Text>
                            </Flex>
                          )}
                        </Flex>
                      </Box>
                      <Box sx={{ mt: 4 }}>
                        {commentDk && currentCulture === "da" && (
                          <Text sx={{ textStyles }}>
                            <span>{commentDk}</span>
                          </Text>
                        )}
                        {commentEn && currentCulture === "en-us" && (
                          <Text sx={textStyles}>
                            <span>{commentEn}</span>
                          </Text>
                        )}
                      </Box>
                    </motion.div>
                    <Box
                      sx={{
                        pt: "10",
                        mt: "auto",
                        display: "flex",
                        flexWrap: "wrap",
                        rowGap: [4, 0],
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        variant="green"
                        outline
                        icon={
                          <Icon
                            svg={MediaSliderArrow}
                            sx={{ transform: "rotate(180deg)" }}
                          />
                        }
                        onClick={closeDetails}
                        sx={{
                          marginRight: "auto",
                          width: ["full", "auto"],
                          order: [4, 0],
                        }}
                      >
                        {get(
                          DICTIONARY_MAP.kiosk.bottle_details_back,
                          currentCulture
                        ) || "Back"}
                      </Button>
                    </Box>
                    {isMobile && (
                      <React.Fragment>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 11,
                          }}
                        >
                          <button
                            disabled={currentBottleIndex === 0}
                            onClick={() => handleBottleIndex("prev")}
                            sx={buttonStyles(currentBottleIndex === 0)}
                          >
                            <Box
                              sx={{
                                ...resetPrevNextButton,
                                ...buttonLeftStyle,
                              }}
                            >
                              <Box sx={leftArrowSpanStyles}>
                                <Icon
                                  sx={leftArrowStyles}
                                  svg={MediaSliderArrow}
                                />
                              </Box>
                              <Icon
                                sx={ellipseStyles}
                                svg={MediaSliderEllipse}
                              />
                            </Box>
                            {get(
                              DICTIONARY_MAP.kiosk.bottle_details_prev,
                              currentCulture
                            )}
                          </button>
                          <button
                            disabled={
                              currentBottleIndex === hits.length - 1 &&
                              isLastPage
                            }
                            onClick={() => handleBottleIndex("next")}
                            sx={buttonStyles(
                              currentBottleIndex === hits.length - 1 &&
                                isLastPage
                            )}
                          >
                            {get(
                              DICTIONARY_MAP.kiosk.bottle_details_next,
                              currentCulture
                            )}
                            <Box
                              sx={{
                                ...resetPrevNextButton,
                                ...buttonRightStyle,
                              }}
                            >
                              <Box sx={rightArrowSpanStyles}>
                                <Icon
                                  sx={rightArrowStyles}
                                  svg={MediaSliderArrow}
                                />
                              </Box>
                              <Icon
                                sx={ellipseStyles}
                                svg={MediaSliderEllipse}
                              />
                            </Box>
                          </button>
                        </Box>
                      </React.Fragment>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    backgroundColor: "white",
                    width: ["100%", "100%", "43.75%"],
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                  id={"display"}
                >
                  <BeerSlider
                    slides={slides}
                    cbKey={""}
                    key={1}
                    alias={""}
                    themes={[]}
                    taxonomies={[]}
                    shortCodes={[]}
                  />
                </Box>
              </Flex>
            </Box>
          )}
        </Container>
      </Box>
    </div>
  );
};

export { BottleDetails };
